import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

const CRED_TYPES = [
  { key: 'commission',       label: 'Notary Commission Certificate',      required: true,  icon: '📜' },
  { key: 'eo_insurance',     label: 'E&O Insurance Declaration Page',     required: true,  icon: '🛡️' },
  { key: 'government_id',    label: 'Government-issued Photo ID',          required: true,  icon: '🪪' },
  { key: 'background_check', label: 'Background Check (within 12 months)',required: true,  icon: '🔍' },
  { key: 'w9_form',          label: 'W-9 Form (completed & signed)',       required: true,  icon: '📋' },
  { key: 'nna_cert',         label: 'NNA Certification',                   required: false, icon: '🏅' },
]

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  :focus-visible { outline: 2px solid #4D4E8D; outline-offset: 3px; border-radius: 6px; }
  .lca-skip { position:absolute; top:-100px; left:16px; background:#4D4E8D; color:#fff; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
  .lca-skip:focus { top:16px; }

  .pa-wrap { min-height:100vh; background:#E9EAEC; display:flex; flex-direction:column; align-items:center; padding:32px 16px; font-family:'Plus Jakarta Sans',sans-serif; }
  .pa-card { background:#fff; border:1px solid #E2E4E7; border-radius:20px; padding:32px 28px; width:100%; max-width:560px; margin-bottom:32px; box-shadow:0 14px 36px rgba(21,23,21,0.08); font-family:'Plus Jakarta Sans',sans-serif; }
  @media(max-width:520px){ .pa-card { padding:24px 16px; } }

  .pa-step-bar { display:flex; gap:6px; margin-bottom:8px; }
  .pa-step-dot { flex:1; height:5px; border-radius:3px; transition:background 0.2s; }
  .pa-step-dot.done { background:#5E8A72; }
  .pa-step-dot.active { background:#4D4E8D; }
  .pa-step-dot.todo { background:#E2E4E7; }

  .pa-cred-item { display:flex; align-items:center; gap:12px; padding:14px 16px; border:1.5px solid #D7DADE; border-radius:12px; margin-bottom:10px; transition:border-color 0.15s; background:#fff; cursor:pointer; position:relative; }
  .pa-cred-item.uploaded { border-color:#5E8A72; background:#EAF2EE; }
  .pa-cred-item.uploading { border-color:#4D4E8D; background:#EEF0F8; }
  .pa-cred-item:hover { border-color:#4D4E8D; }
  .pa-cred-icon { font-size:22px; flex-shrink:0; }
  .pa-cred-label { font-size:13px; font-weight:600; color:#151715; flex:1; }
  .pa-cred-req { font-size:10px; color:#A2A6AA; margin-top:2px; }
  .pa-cred-status { font-size:20px; flex-shrink:0; }

  .pa-progress-hero { background:linear-gradient(135deg,#4D4E8D,#41427a); border-radius:14px; padding:20px; margin-bottom:20px; }
  .pa-checklist-item { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid #EEEFF1; font-size:13px; }
  .pa-checklist-item:last-child { border-bottom:none; }
  .pa-checklist-dot { width:20px; height:20px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }
  .pa-checklist-dot.done { background:#EAF2EE; color:#5E8A72; }
  .pa-checklist-dot.todo { background:#F5F6F7; color:#A2A6AA; border:1px solid #E2E4E7; }
`

export default function PendingApproval() {
  const { profile, user, signOut } = useAuth()
  const [creds,       setCreds]       = useState([])
  const [uploading,   setUploading]   = useState(null)
  const [activeInput, setActiveInput] = useState(null)
  const [submitted,   setSubmitted]   = useState(false)

  // ✅ Detect if coming fresh from registration — show success screen not upload screen!!
  const justRegistered = new URLSearchParams(window.location.search).get('registered') === 'true'

  useEffect(() => {
    if (!user) return
    supabase.from('notary_docs').select('*').eq('notary_id', user.id)
      .then(({ data }) => setCreds(data || []))
  }, [user])

  // ✅ Fixed sign out function
  async function handleSignOut() {
    try {
      await signOut()
      window.location.href = '/login'
    } catch (err) {
      window.location.href = '/login'
    }
  }

  // Check if a doc type has been uploaded
  function isUploaded(key) {
    return creds.some(c => c.doc_type === key)
  }

  // Upload handler
  async function handleUpload(e, credType) {
    const file = e.target.files[0]
    if (!file || !user) return
    setUploading(credType.key)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${user.id}/${credType.key}-${Date.now()}.${ext}`
      const { error: uploadErr } = await supabase.storage.from('credentials').upload(path, file)
      if (uploadErr) throw uploadErr
      const { data: { publicUrl } } = supabase.storage.from('credentials').getPublicUrl(path)
      await supabase.from('notary_docs').insert({
        notary_id: user.id,
        doc_type:  credType.key,
        doc_label: credType.label,
        file_url:  publicUrl,
        status:    'pending',
      })
      toast.success(`✅ ${credType.label} uploaded!!`)
      // Refresh creds list
      supabase.from('notary_docs').select('*').eq('notary_id', user.id)
        .then(({ data }) => setCreds(data || []))
    } catch (err) {
      toast.error('Upload failed: ' + err.message)
    } finally {
      setUploading(null)
    }
  }

  // Progress calculations
  const requiredKeys    = CRED_TYPES.filter(c => c.required).map(c => c.key)
  const uploadedKeys    = creds.map(c => c.doc_type)
  const requiredDone    = requiredKeys.filter(k => uploadedKeys.includes(k)).length
  const allRequiredDone = requiredDone === requiredKeys.length
  const pct             = Math.round((requiredDone / requiredKeys.length) * 100)

  // Onboarding steps for checklist
  const steps = [
    { label: 'Personal information',     done: true },
    { label: 'Commission & experience',  done: !!profile?.notaries?.commission_state || true },
    { label: 'Required documents',       done: allRequiredDone },
    { label: 'Admin review & approval',  done: false },
  ]

  if (profile?.role !== 'notary') {
    return (
      <div style={{ minHeight:'100vh', background:'#E9EAEC', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <style>{STYLES}</style>
        <div className="pa-card" style={{ textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:16 }}>⏳</div>
          <h1 style={{ fontSize:22, color:'#151715', marginBottom:8 }}>Account Under Review</h1>
          <p style={{ fontSize:13, color:'#5C6065', marginBottom:24, lineHeight:1.7 }}>
            Thank you, <strong>{profile?.full_name}</strong>!! Our team is reviewing your account and will approve it within 1 business day.
          </p>
          <div style={{ fontSize:12, color:'#5C6065', marginBottom:20 }}>
            Questions? Email <a href="mailto:nancy@notarysigningvault.com" style={{ color:'#4D4E8D' }}>nancy@notarysigningvault.com</a>
          </div>
          <button onClick={handleSignOut} className="btn btn-outline" style={{ width:'100%' }} type="button">Sign out</button>
        </div>
      </div>
    )
  }

  // ✅ FRESH REGISTRATION SUCCESS SCREEN — shown right after submitting application!!
  if (justRegistered) {
    return (
      <div style={{ minHeight:'100vh', background:'#E9EAEC', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24 }}>
        <style>{STYLES}</style>
        <img src="/nsv_emblem.png?v=2" alt="Notary Signing Vault, LLC" style={{ height:70, width:'auto', objectFit:'contain', marginBottom:24, filter:'drop-shadow(0 6px 18px rgba(77,78,141,0.20))' }} onError={e => { e.target.style.display='none' }} />
        <div className="pa-card" style={{ textAlign:'center', maxWidth:520 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
          <h1 style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#151715', marginBottom:8 }}>
            Application Submitted!!
          </h1>
          <p style={{ fontSize:14, color:'#5C6065', lineHeight:1.8, marginBottom:20 }}>
            Thank you, <strong style={{ color:'#151715' }}>{profile?.full_name}</strong>!!<br />
            Your application has been received and your documents are under review!!
          </p>
          <div style={{ background:'#EAF2EE', border:'1px solid rgba(45,122,79,0.2)', borderRadius:12, padding:16, marginBottom:20, textAlign:'left' }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#5E8A72', marginBottom:10 }}>✅ What happens next:</div>
            {[
              'Our Team will review your application within 1 business day',
              'You\'ll receive a welcome email with your NSV Vendor Number',
              'Once approved you can log in and start accepting assignments!!',
            ].map((s, i) => (
              <div key={i} style={{ display:'flex', gap:8, fontSize:12, color:'#4A7A5C', marginBottom:6 }}>
                <span style={{ color:'#5E8A72', fontWeight:600, flexShrink:0 }}>{i+1}.</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize:12, color:'#5C6065', marginBottom:20 }}>
            Questions? Call <a href="tel:9048650133" style={{ color:'#4D4E8D', fontWeight:500 }}>(904) 865-0133</a> or email <a href="mailto:nancy@notarysigningvault.com" style={{ color:'#4D4E8D', fontWeight:500 }}>nancy@notarysigningvault.com</a>
          </div>
          <button onClick={handleSignOut} className="btn btn-outline" style={{ width:'100%' }} type="button">Sign out</button>
        </div>
      </div>
    )
  }

  return (
    <div className="pa-wrap">
      <style>{STYLES}</style>
      <a href="#pending-main" className="lca-skip">Skip to main content</a>

      {/* LOGO */}
      <img src="/nsv_emblem.png?v=2" alt="Notary Signing Vault, LLC" style={{ height:70, width:'auto', objectFit:'contain', marginBottom:24, filter:'drop-shadow(0 6px 18px rgba(77,78,141,0.20))' }} onError={e => { e.target.style.display='none' }} />

      <main id="pending-main" tabIndex={-1} style={{ width:'100%', maxWidth:560 }}>

        {/* HERO STATUS CARD */}
        <div className="pa-card" style={{ marginBottom:16 }}>
          <div className="pa-progress-hero">
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
              <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'2px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                {allRequiredDone ? '🎉' : '⏳'}
              </div>
              <div>
                <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, color:'#fff', fontWeight:500 }}>
                  {allRequiredDone ? 'Application Complete!!' : 'Complete Your Application'}
                </div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>
                  {allRequiredDone
                    ? 'Our Team will review and approve your account within 1 business day!!'
                    : `Upload your required documents to complete your application — ${requiredKeys.length - requiredDone} remaining`
                  }
                </div>
              </div>
            </div>
            <div style={{ marginBottom:6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'rgba(255,255,255,0.5)', marginBottom:6 }}>
                <span>Application progress</span>
                <span style={{ color:'#fff', fontWeight:700 }}>{pct}% complete</span>
              </div>
              <div style={{ height:6, background:'rgba(255,255,255,0.1)', borderRadius:20 }}>
                <div style={{ height:6, borderRadius:20, background:'linear-gradient(to right,#8BB6A2,#fff)', width:`${pct}%`, transition:'width 0.4s' }} />
              </div>
            </div>
          </div>

          {/* ONBOARDING CHECKLIST */}
          <div style={{ marginBottom:4 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#5C6065', letterSpacing:'0.08em', marginBottom:12 }}>ONBOARDING CHECKLIST</div>
            {steps.map((s, i) => (
              <div key={i} className="pa-checklist-item">
                <div className={`pa-checklist-dot ${s.done ? 'done' : 'todo'}`}>{s.done ? '✓' : i + 1}</div>
                <span style={{ color: s.done ? '#5E8A72' : '#3A3D40', fontWeight: s.done ? 500 : 400 }}>{s.label}</span>
                {s.done && <span style={{ fontSize:10, color:'#5E8A72', marginLeft:'auto' }}>Complete</span>}
                {!s.done && i === 2 && <span style={{ fontSize:10, color:'#5E8A72', marginLeft:'auto' }}>Uploaded in Step 5 ✓</span>}
                {!s.done && i === 3 && <span style={{ fontSize:10, color:'#A2A6AA', marginLeft:'auto' }}>Pending</span>}
              </div>
            ))}
          </div>
        </div>

        {/* ✅ DOCUMENT STATUS CARD — no duplicate upload!! Just show what's been uploaded!! */}
        <div className="pa-card">
          <h2 style={{ fontSize:16, fontWeight:600, color:'#151715', marginBottom:4 }}>
            📄 Document Status
          </h2>
          <p style={{ fontSize:12, color:'#5C6065', marginBottom:20, lineHeight:1.6 }}>
            {allRequiredDone
              ? 'All required documents received!! Our Team will review your application shortly!!'
              : 'Documents uploaded during registration are shown below. Log back in to upload any missing documents!!'}
          </p>

          {CRED_TYPES.map(credType => {
            const uploaded = isUploaded(credType.key)
            return (
              <div key={credType.key} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #EEEFF1' }}>
                <div style={{ fontSize:20 }}>{credType.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:'#151715' }}>{credType.label}</div>
                  <div style={{ fontSize:11, color: uploaded ? '#5E8A72' : '#A2A6AA' }}>
                    {uploaded ? '✓ Received — pending review' : credType.required ? 'Not yet received' : 'Optional'}
                  </div>
                </div>
                <div style={{ fontSize:18 }}>{uploaded ? '✅' : credType.required ? '⏳' : '—'}</div>
              </div>
            )
          })}

          {allRequiredDone && (
            <div style={{ background:'#EAF2EE', border:'1px solid rgba(45,122,79,0.3)', borderRadius:12, padding:16, marginTop:16, textAlign:'center' }}>
              <div style={{ fontSize:24, marginBottom:8 }}>🎉</div>
              <div style={{ fontSize:14, fontWeight:600, color:'#5E8A72', marginBottom:4 }}>All documents received!!</div>
              <div style={{ fontSize:12, color:'#4A7A5C', lineHeight:1.6 }}>
                Our Team will review your application and approve your account within <strong>1 business day</strong>. You'll receive an email with your NSV Vendor Number once approved!!
              </div>
            </div>
          )}

          <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid #D7DADE', textAlign:'center' }}>
            <div style={{ fontSize:12, color:'#5C6065', marginBottom:16 }}>
              Need help? Email{' '}
              <a href="mailto:nancy@notarysigningvault.com" style={{ color:'#4D4E8D', fontWeight:500 }}>nancy@notarysigningvault.com</a>
              {' '}or call{' '}
              <a href="tel:9048650133" style={{ color:'#4D4E8D', fontWeight:500 }}>(904) 865-0133</a>
            </div>
            <button onClick={handleSignOut} className="btn btn-outline" style={{ width:'100%' }} type="button">
              Sign out
            </button>
          </div>
        </div>

      </main>
    </div>
  )
}
